import { LocalizedText, useSourceLanguage } from "../i18n/LocalizedCopy";
// src/auth/LoginPage.js
import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useI18n } from "../i18n/LanguageContext";
import CosmicValidation from "../components/CosmicUltra+";
import { useAuth } from "../context/AuthContext";
import {
  resolveLoginReturnFromSearch,
  sanitizeLoginReturnPath,
} from "../missions/rubricRegistry";
import { playNotificationSound } from "../utils/notificationAudio";
import { speakLiaLoginVoice } from "../utils/rewardsVoice";
import { getOrCreateDeviceId } from "../utils/deviceTrust";
import { clearPendingMfaSetup } from "./mfaSetupSession";
import { claimPendingIntent } from "./accountFlowApi";
import "./LoginPage.css";

const LOGIN_RETURN_STORAGE_KEY = "eternua.auth.return_to";

function readStoredReturnPath() {
  try {
    return window.sessionStorage.getItem(LOGIN_RETURN_STORAGE_KEY) || "";
  } catch (_error) {
    return "";
  }
}

function storeReturnPath(value) {
  try {
    if (value) window.sessionStorage.setItem(LOGIN_RETURN_STORAGE_KEY, value);
    else window.sessionStorage.removeItem(LOGIN_RETURN_STORAGE_KEY);
  } catch (_error) {
    // Storage can be unavailable in privacy mode.
  }
}

function LoginIcon({ name }) {
  useSourceLanguage();
  const commonProps = {
    "aria-hidden": true,
    className: "login-icon",
    fill: "none",
    viewBox: "0 0 24 24",
  };

  if (name === "community") {
    return (
      <svg {...commonProps}>
        <circle cx="9" cy="8" r="3" />
        <path d="M3.5 19v-1.5A4.5 4.5 0 0 1 8 13h2a4.5 4.5 0 0 1 4.5 4.5V19" />
        <circle cx="17" cy="9" r="2.25" />
        <path d="M15.5 14.25h1.75A3.25 3.25 0 0 1 20.5 17.5V19" />
      </svg>
    );
  }

  if (name === "market") {
    return (
      <svg {...commonProps}>
        <path d="M5.5 8.5h13l-.65 11h-11.7l-.65-11Z" />
        <path d="M9 9V6.75a3 3 0 0 1 6 0V9" />
      </svg>
    );
  }

  if (name === "calendar") {
    return (
      <svg {...commonProps}>
        <rect x="4" y="5.5" width="16" height="14" rx="2.5" />
        <path d="M8 3.75v3.5M16 3.75v3.5M4 9.5h16" />
        <path d="m12 12.2.85 1.7 1.9.28-1.38 1.34.33 1.9-1.7-.9-1.7.9.33-1.9-1.38-1.34 1.9-.28.85-1.7Z" />
      </svg>
    );
  }

  if (name === "shield") {
    return (
      <svg {...commonProps}>
        <path d="M12 3.5 19 6v5.2c0 4.45-2.7 7.45-7 9.3-4.3-1.85-7-4.85-7-9.3V6l7-2.5Z" />
        <path d="m9 12 2 2 4-4" />
      </svg>
    );
  }

  if (name === "lock") {
    return (
      <svg {...commonProps}>
        <rect x="5" y="10" width="14" height="10" rx="2.5" />
        <path d="M8.5 10V7.5a3.5 3.5 0 0 1 7 0V10M12 14v2.5" />
      </svg>
    );
  }

  if (name === "spark") {
    return (
      <svg {...commonProps}>
        <path d="M12 3.5 13.6 9l5.4 1.6-5.4 1.6L12 17.5l-1.6-5.3L5 10.6 10.4 9 12 3.5Z" />
        <path d="m18.2 15 .65 2.15L21 17.8l-2.15.65-.65 2.05-.65-2.05-2.05-.65 2.05-.65.65-2.15Z" />
      </svg>
    );
  }

  if (name === "mail") {
    return (
      <svg {...commonProps}>
        <rect x="3" y="5" width="18" height="14" rx="2.5" />
        <path d="m4.5 7 7.5 6 7.5-6" />
      </svg>
    );
  }

  if (name === "eye-off") {
    return (
      <svg {...commonProps}>
        <path d="M3 3 21 21M10.6 10.6a2 2 0 0 0 2.8 2.8M9.2 5.4A9.7 9.7 0 0 1 12 5c5.2 0 8.5 4.8 9 7-.18.8-.73 1.87-1.6 2.93M6.05 6.05C4.25 7.27 3.25 9.15 3 12c.5 2.2 3.8 7 9 7 1.25 0 2.4-.28 3.43-.75" />
      </svg>
    );
  }

  if (name === "eye") {
    return (
      <svg {...commonProps}>
        <path d="M3 12c.5-2.2 3.8-7 9-7s8.5 4.8 9 7c-.5 2.2-3.8 7-9 7s-8.5-4.8-9-7Z" />
        <circle cx="12" cy="12" r="2.5" />
      </svg>
    );
  }

  return null;
}

export default function LoginPage({ embedded = false }) {
  useSourceLanguage();
  const errorRef = useRef(null);
  const { t } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const { login, verifyMfaLogin } = useAuth();
  const requestedReturnTo = resolveLoginReturnFromSearch(
    location?.search,
    ""
  );
  const storedReturnTo = sanitizeLoginReturnPath(readStoredReturnPath(), "");
  const returnTo = requestedReturnTo || storedReturnTo || "/";
  const intentId = String(new URLSearchParams(location.search || "").get("intent") || "").trim();

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [validated, setValidated] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaTicket, setMfaTicket] = useState("");
  const [mfaCode, setMfaCode] = useState("");
  const [mfaSecondsRemaining, setMfaSecondsRemaining] = useState(0);
  const [rememberDevice, setRememberDevice] = useState(true);

  const resetError = () => setError("");
  useEffect(() => {
    if (requestedReturnTo) storeReturnPath(requestedReturnTo);
  }, [requestedReturnTo]);

  useEffect(() => {
    // Les anciennes inscriptions imposaient la MFA avant la connexion. Ces
    // marqueurs ne doivent plus bloquer le démarrage : la MFA se choisit dans
    // le profil et reste exigée à la connexion uniquement si elle est activée.
    clearPendingMfaSetup();
  }, []);

  useEffect(() => {
    if (!embedded || typeof document === "undefined") return undefined;
    document.body.classList.add("eternua-embedded-login");
    return () => document.body.classList.remove("eternua-embedded-login");
  }, [embedded]);

  useEffect(() => {
    if (!mfaRequired || mfaSecondsRemaining <= 0) return undefined;
    const timeoutId = window.setTimeout(() => {
      setMfaSecondsRemaining((seconds) => Math.max(seconds - 1, 0));
    }, 1000);
    return () => window.clearTimeout(timeoutId);
  }, [mfaRequired, mfaSecondsRemaining]);

  useEffect(() => {
    if (!mfaRequired || !mfaTicket || mfaSecondsRemaining > 0) return;
    setMfaRequired(false);
    setMfaTicket("");
    setMfaCode("");
    setError(t("login.expired", "Ticket MFA expiré. Reconnecte-toi."));
  }, [mfaRequired, mfaSecondsRemaining, mfaTicket, t]);

  const handleReturnToCredentials = () => {
    setMfaRequired(false);
    setMfaTicket("");
    setMfaCode("");
    setMfaSecondsRemaining(0);
    setError("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    resetError();

    if (mfaRequired && !/^\d{6}$/.test(mfaCode)) {
      setError(t("login.mfaInvalid", "Entre les six chiffres affichés dans Google Authenticator."));
      return;
    }

    setLoading(true);

    try {
      let authenticatedUser = null;
      if (!mfaRequired) {
        const result = await login((username || "").trim(), password, {
          deviceId: getOrCreateDeviceId(),
          rememberDevice,
        });
        if (result?.mfaRequired) {
          setMfaRequired(true);
          setMfaTicket(String(result.mfaTicket || ""));
          setMfaCode("");
          setMfaSecondsRemaining(
            Number(result.mfaExpiresIn) > 0 ? Number(result.mfaExpiresIn) : 300
          );
          setError("");
          return;
        }
        authenticatedUser = result;
        clearPendingMfaSetup();
      } else {
        authenticatedUser = await verifyMfaLogin(mfaTicket, mfaCode, {
          deviceId: getOrCreateDeviceId(),
          rememberDevice,
        });
      }

      if (authenticatedUser?.access?.email_verified === false) {
        const query = new URLSearchParams({ email: authenticatedUser.email || username });
        if (intentId) query.set("intent", intentId);
        navigate(`/verify-email?${query.toString()}`, { replace: true });
        return;
      }

      if (intentId) {
        await claimPendingIntent(intentId);
        window.sessionStorage.setItem("eternua.pendingIntentId", intentId);
      }

      setValidated(true);
      playNotificationSound("/sounds/gong-divin-eternua.wav", { volume: 1 });
      speakLiaLoginVoice(
        t("login.voice", "Coucou, c’est LIA. Je suis heureuse de te retrouver. Ton espace Eternua est prêt. Explore, grandis et profite de chaque opportunité. Bienvenue dans notre espace de confiance.")
      );
      if (!embedded) {
        storeReturnPath("");
        const destination = intentId
          ? `/onboarding?intent=${encodeURIComponent(intentId)}`
          : returnTo === "/" ? "/onboarding" : returnTo;
        setTimeout(() => navigate(destination, { replace: true }), 450);
      }
    } catch (err) {
      const errorCode = String(err?.data?.code || "");
      if (["mfa_ticket_expired", "mfa_attempts_exceeded"].includes(errorCode)) {
        setMfaRequired(false);
        setMfaTicket("");
        setMfaCode("");
        setMfaSecondsRemaining(0);
      } else if (errorCode === "mfa_code_invalid") {
        setMfaCode("");
      }
      const networkFailure = err instanceof TypeError
        && /fetch|network|load failed/i.test(err.message || "");
      setError(networkFailure
        ? t("login.networkError", "Impossible de joindre le serveur de connexion. Vérifie que le serveur est disponible, puis réessaie.")
        : err?.message || t("login.credentialsError", "Identifiants incorrects. Réessaie avec douceur."));
    } finally {
      setLoading(false);
    }
  };

  const inputInvalidProps = { "aria-invalid": Boolean(error) };
  useEffect(() => {
    if (error) errorRef.current?.focus();
  }, [error]);
  const rememberInputId = mfaRequired ? "login-trust-device" : "login-remember-device";

  return (
    <>
      <CosmicValidation visible={validated} />

      <main
        className={`login-page auth-atelier${embedded ? " login-page--embedded" : ""}`}
      >
        <div className="login-layout">
          <section className="auth-story" aria-labelledby="login-page-title">
            <p className="auth-story__eyebrow"><span />{" "}{<LocalizedText source={t("login.brand", "LA CONVERGENCE DE L’INFINI")} />}</p>
            <div className="auth-story__copy">
              <p className="auth-story__chapter">{<LocalizedText source={t("login.chapter", "VOTRE UNIVERS VOUS ATTEND")} />}</p>
              <h1 id="login-page-title">{<LocalizedText source={t("login.headlineStart", "Retrouvez ce qui")} />}<br />{<LocalizedText source={t("login.headlineMiddle", "vous")} />}{" "}<em>{<LocalizedText source={t("login.headlineEnd", "relie.")} />}</em></h1>
              <p>{<LocalizedText source={t("login.lead", "Les conversations qui comptent. Les projets qui vous ressemblent. Tout votre univers, à nouveau à portée de main.")} />}</p>
            </div>
            <div className="auth-sculpture" aria-hidden="true"><span /><span /><span /><i>∞</i><b /></div>
            <div className="auth-story__bottom"><span>{<LocalizedText source={t("login.storyBottom", "Des liens. Des idées. Des possibles.")} />}</span><span><LocalizedText source={"ETERNUA"} /></span></div>
          </section>

          <section className="login-panel" aria-labelledby="login-form-title">
            <div className="auth-panel-mark" aria-hidden="true">∞</div>
            <header className="login-panel__header">
              <p className="login-panel__eyebrow">{<LocalizedText source={t("login.personal", "Votre espace personnel")} />}</p>
              <h2 id="login-form-title">{<LocalizedText source={t("login.welcome", "Bienvenue chez vous")} />}</h2>
              <p>{<LocalizedText source={t("login.intro", "Vos liens, vos projets et vos envies vous attendent.")} />}</p>
            </header>

            {mfaRequired ? (
              <div className="login-mfa-banner" role="status">
                <span className="login-mfa-banner__icon">
                  <LoginIcon name="shield" />
                </span>
                <div>
                  <strong>{<LocalizedText source={t("login.mfaRequired", "Google Authenticator requis")} />}</strong>
                  <p>
                    {<LocalizedText source={t("login.mfaIntro", "Entre le code à 6 chiffres pour finaliser la connexion")} />}
                    {mfaSecondsRemaining > 0
                      ? ` · ${Math.floor(mfaSecondsRemaining / 60)}:${String(
                          mfaSecondsRemaining % 60
                        ).padStart(2, "0")}`
                      : ""}
                  </p>
                </div>
              </div>
            ) : null}

            {error ? (
              <div id="login-error" className="login-error" role="alert" ref={errorRef} tabIndex={-1}>
                <span aria-hidden="true">!</span>
                <p>{<LocalizedText source={error} />}</p>
              </div>
            ) : null}

            <form className="login-form" onSubmit={handleSubmit} noValidate={false}>
              {!mfaRequired ? (
                <>
                  <div className="login-field-group">
                    <label htmlFor="login-username">{<LocalizedText source={t("login.username", "E-mail ou nom d’utilisateur")} />}</label>
                    <div className="login-field-shell">
                      <span className="login-field-icon">
                        <LoginIcon name="mail" />
                      </span>
                      <input
                        id="login-username"
                        name="username"
                        type="text"
                        placeholder={t("login.usernamePlaceholder", "vous@exemple.fr ou votre identifiant")}
                        value={username}
                        onChange={(event) => setUsername(event.target.value)}
                        onFocus={resetError}
                        autoComplete="username"
                        required
                        disabled={loading}
                        aria-describedby={error ? "login-error" : undefined}
                        {...inputInvalidProps}
                      />
                    </div>
                  </div>

                  <div className="login-field-group">
                    <label htmlFor="login-password">{<LocalizedText source={t("login.password", "Mot de passe")} />}</label>
                    <div className="login-field-shell">
                      <span className="login-field-icon">
                        <LoginIcon name="lock" />
                      </span>
                      <input
                        id="login-password"
                        name="password"
                        type={showPassword ? "text" : "password"}
                        placeholder={t("login.passwordPlaceholder", "Votre mot de passe")}
                        value={password}
                        onChange={(event) => setPassword(event.target.value)}
                        onFocus={resetError}
                        autoComplete="current-password"
                        required
                        disabled={loading}
                        aria-describedby={error ? "login-error" : undefined}
                        {...inputInvalidProps}
                      />
                      <button
                        className="login-password-toggle"
                        type="button"
                        onClick={() => setShowPassword((visible) => !visible)}
                        aria-label={showPassword ? t("login.hidePassword", "Masquer le mot de passe") : t("login.showPassword", "Afficher le mot de passe")}
                        aria-pressed={showPassword}
                        disabled={loading}
                      >
                        <LoginIcon name={showPassword ? "eye-off" : "eye"} />
                      </button>
                    </div>
                  </div>

                  <div className="login-form-options">
                    <label className="login-checkbox" htmlFor={rememberInputId}>
                      <input
                        id={rememberInputId}
                        type="checkbox"
                        checked={rememberDevice}
                        onChange={(event) => setRememberDevice(event.target.checked)}
                        disabled={loading}
                      />
                      <span aria-hidden="true" />{<LocalizedText source={t("login.remember", "Se souvenir de cet appareil")} />}</label>
                    <Link
                      to="/forgot-password"
                      className="login-forgot-link"
                      onClick={() => {
                        playNotificationSound("/sounds/gong-divin-eternua.wav", { volume: 1 });
                      }}
                    >{<LocalizedText source={t("login.forgot", "Mot de passe oublié ?")} />}</Link>
                  </div>
                </>
              ) : (
                <>
                  <div className="login-field-group">
                    <label htmlFor="login-mfa-code">{<LocalizedText source={t("login.mfaCode", "Code Google Authenticator")} />}</label>
                    <div className="login-field-shell login-field-shell--mfa">
                      <span className="login-field-icon">
                        <LoginIcon name="lock" />
                      </span>
                      <input
                        id="login-mfa-code"
                        name="mfa-code"
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]{6}"
                        maxLength={6}
                        placeholder="123456"
                        value={mfaCode}
                        onChange={(event) =>
                          setMfaCode((event.target.value || "").replace(/\D/g, "").slice(0, 6))
                        }
                        onFocus={resetError}
                        autoComplete="one-time-code"
                        aria-describedby={error ? "login-mfa-help login-error" : "login-mfa-help"}
                        required
                        disabled={loading}
                        {...inputInvalidProps}
                      />
                    </div>
                    <p id="login-mfa-help" className="login-field-help">{<LocalizedText source={t("login.mfaHelp", "Saisis uniquement les six chiffres de l’application.")} />}</p>
                  </div>

                  <label className="login-checkbox login-checkbox--mfa" htmlFor={rememberInputId}>
                    <input
                      id={rememberInputId}
                      type="checkbox"
                      checked={rememberDevice}
                      onChange={(event) => setRememberDevice(event.target.checked)}
                      disabled={loading}
                    />
                    <span aria-hidden="true" />{<LocalizedText source={t("login.trustDevice", "Faire confiance à cet appareil pendant 30 jours")} />}</label>
                </>
              )}

              <p className="login-sr-only" aria-live="polite">
                {loading ? (mfaRequired ? <LocalizedText source={t("login.validating", "Validation du code en cours")} /> : <LocalizedText source={t("login.connecting", "Connexion en cours")} />) : ""}
              </p>

              <button
                className="login-submit"
                type="submit"
                disabled={loading || (mfaRequired && mfaCode.length !== 6)}
              >
                <span aria-hidden="true" />
                {loading ? <LocalizedText source={t("login.loading", "Connexion en cours…")} /> : mfaRequired ? <LocalizedText source={t("login.validate", "Valider le code")} /> : <LocalizedText source={t("login.signin", "Se connecter")} />}
              </button>

              {mfaRequired ? (
                <button
                  className="login-back-button"
                  type="button"
                  onClick={handleReturnToCredentials}
                  disabled={loading}
                >{<LocalizedText source={t("login.back", "Retour à l’identifiant et au mot de passe")} />}</button>
              ) : null}
            </form>

            <div className="login-register-separator" aria-hidden="true">
              <span />
              <p>{<LocalizedText source={t("login.new", "Nouveau sur Eternua ?")} />}</p>
              <span />
            </div>

            <Link
              to="/register"
              className="login-register-button"
              onClick={() => {
                playNotificationSound("/sounds/gong-divin-eternua.wav", { volume: 1 });
              }}
            >{<LocalizedText source={t("login.create", "Créer mon compte gratuitement")} />}</Link>

            <p className="login-privacy-note">
              <LoginIcon name="shield" />{<LocalizedText source={t("login.private", "Vos informations restent privées et protégées.")} />}</p>
          </section>
        </div>
        <footer className="auth-atelier-footer"><span>{<LocalizedText source={t("login.footer", "Un espace pour ce qui compte.")} />}</span><nav aria-label={t("login.footerNav", "Informations")}><Link to="/confidentialite">{<LocalizedText source={t("login.privacy", "Confidentialité")} />}</Link><Link to="/cgu">{<LocalizedText source={t("login.terms", "Conditions d’utilisation")} />}</Link></nav></footer>
      </main>
    </>
  );
}
